/* eslint-disable custom-eslint-rules/force-logicalOperandsMustBeBoolean */
import { motion } from "framer-motion";
import { ReactElement, useRef } from "react";
import { useTheme } from "styled-components";
import { ArrowDown02Linear } from "../Iconsax/ArrowDown02Linear.js";
import { CloseLinear } from "../Iconsax/CloseLinear.js";
import { ICONSAX_SIZE } from "../Iconsax/Iconsax.types.js";
import { Input } from "../Input/Input.js";
import { Popover } from "../Popover/Popover.js";
import { PopoverItem } from "../Popover/components/PopoverItem/PopoverItem.js";
import { rem } from "../Theme/Theme.constants.js";
import { useTranslationsContext } from "../TranslationsProvider/TranslationsProvider.context.js";
import { InputWrapper } from "./SelectV2.style.js";
import { SelectV2Props, SelectV2Value } from "./SelectV2.types.js";
import { OptionsMenu } from "./components/OptionsMenu.js";
import { SelectedTags } from "./components/SelectedTags.js";
import { TagOption } from "./components/TagOption.js";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation.js";
import { useSelectMenu } from "./hooks/useSelectMenu.js";
import { useSelectValue } from "./hooks/useSelectValue.js";
/**
 * SelectV2 component for rendering a customizable dropdown.
 *
 * Features:
 * - Single or multiple selection
 * - Searchable options
 * - Custom option rendering
 * - Creation of new options
 * - Loading states
 * - Keyboard navigation
 *
 * @example
 * ```tsx
 * <SelectV2
 *   options={[
 *     { value: 1, label: "Option 1" },
 *     { value: 2, label: "Option 2" },
 *   ]}
 *   value={1}
 *   onChange={(value) => console.log(value)}
 *   placeholder="Select an option"
 * />
 * ```
 */

const SelectV2 = <Value extends SelectV2Value>({
    id,
    value,
    hasMore,
    loadMore,
    onChange,
    options,
    maxMenuHeight = rem(20),
    placeholder,
    width,
    minWidth,
    multiple = false,
    hideSelectedOptions = false,
    clearable = false,
    showArrowOnHover = false,
    translations: customTranslations,
    creatable,
    isCreating,
    onClickCreate,
    searchable,
    onSearchChange,
    hideCreateLabel = false,
    autoFocus,
    popoverRef,
    popoverChildRef,
    container,
    maxVisibleTags,
    isLoading,
    loader,
    prependElements,
    ...rest
}: SelectV2Props<Value>) => {
    const theme = useTheme();
    const { translations } = useTranslationsContext();
    const inputRef = useRef<HTMLInputElement>(null);

    // Menu state management
    const { isMenuOpen, search, filteredOptions, openMenu, closeMenu, handleSearchChange } = useSelectMenu({
        options,
        multiple: !!multiple,
    });

    // Value management
    const { valueLabels, handleChangeValue, handleOptionClick, clear } = useSelectValue({
        value,
        options,
        onChange: onChange as (value: Value | Value[] | undefined) => void,
        multiple: !!multiple,
        closeMenu,
    });

    // Auto-select single option when there's only one option available
    // useEffect(() => {
    //     const singleOption = options[0];
    //     if (!multiple && options.length === 1 && singleOption && value !== singleOption.value && !props.isLoading) {
    //         (onChange as (value: Value) => void)(singleOption.value);
    //     }
    // }, [multiple, options, value, onChange, props.isLoading]);

    // Keyboard navigation
    const { focusedIndex, handleKeyDown } = useKeyboardNavigation({
        isMenuOpen,
        options: filteredOptions,
        onOptionSelect: handleOptionClick,
        selectedIndex: options.findIndex((option) => option.value === value) || -1,
        openMenu,
        closeMenu,
        searchable: !!searchable,
    });

    // Input click handler
    const onInputClick = () => {
        if (isMenuOpen) closeMenu();
        else {
            openMenu();
            inputRef.current?.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
        }
    };

    // Determine what to display in the input field
    const inputValue = multiple || search !== undefined ? search || "" : valueLabels;

    const renderSelectedTagsSection = (): ReactElement | null => {
        if (multiple && Array.isArray(value) && value.length > 0) {
            return (
                <SelectedTags
                    values={value}
                    options={options}
                    maxDisplayCount={maxVisibleTags}
                    onRemove={(val) => {
                        if (multiple) {
                            const newValues = (value as Value[]).filter((v) => v !== val);
                            handleChangeValue(newValues);
                        }
                    }}
                />
            );
        }
        return null;
    };

    return (
        <Popover
            open={isMenuOpen}
            onOpenChange={(open) => (open ? openMenu() : closeMenu())}
            hideArrow
            positionTop={theme.spacing[4]}
            fitChildrenWidth
            removePadding
            elevation={theme.elevationLevels.max}
            disableClickOnChildren
            popoverRef={popoverRef}
            popoverChildRef={popoverChildRef}
            container={container}
            dropdownContent={
                <OptionsMenu
                    options={options}
                    filteredOptions={filteredOptions}
                    value={value}
                    multiple={!!multiple}
                    maxMenuHeight={typeof maxMenuHeight === "number" ? rem(maxMenuHeight) : maxMenuHeight}
                    focusedIndex={focusedIndex}
                    isLoading={isLoading}
                    loader={loader}
                    hasMore={hasMore}
                    loadMore={loadMore}
                    onOptionClick={handleOptionClick}
                    onClearAll={() => multiple && handleChangeValue([])}
                    search={search}
                    creatable={creatable}
                    isCreating={isCreating}
                    onClickCreate={(v, closeMenu) => {
                        onClickCreate?.(v, closeMenu);
                        handleSearchChange("");
                    }}
                    closeMenu={closeMenu}
                    customTranslations={customTranslations}
                    highlightedIndex={focusedIndex}
                    hideCreateLabel={hideCreateLabel}
                    hideSelectedOptions={hideSelectedOptions}
                    popoverRef={popoverChildRef}
                />
            }
        >
            <InputWrapper width={width} minWidth={minWidth} showArrowOnHover={showArrowOnHover} isMenuOpen={isMenuOpen} ref={popoverRef}>
                <Input
                    id={id}
                    autoFocus={autoFocus}
                    onKeyDown={handleKeyDown}
                    ref={inputRef}
                    appendElements={
                        <>
                            {clearable && inputValue && (
                                <div
                                    style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        clear();
                                    }}
                                >
                                    <CloseLinear size={ICONSAX_SIZE.SMALL} color={theme.grayscale[90]} />
                                </div>
                            )}
                            <motion.div
                                animate={{ rotate: isMenuOpen ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                                style={{ alignItems: "center" }}
                                className="arrow-wrapper"
                            >
                                <ArrowDown02Linear size={ICONSAX_SIZE.SMALL} color={theme.grayscale[90]} />
                            </motion.div>
                        </>
                    }
                    onClick={onInputClick}
                    value={inputValue}
                    prependElements={prependElements || renderSelectedTagsSection()}
                    isStatic={!searchable}
                    placeholder={
                        multiple && Array.isArray(value) && value.length > 0
                            ? undefined
                            : placeholder || customTranslations?.placeholder || translations.select?.placeholder
                    }
                    onChange={(e) => {
                        if (searchable) {
                            onSearchChange?.(e.target.value);
                            handleSearchChange(e.target.value);
                        }
                    }}
                    {...rest}
                />
            </InputWrapper>
        </Popover>
    );
};

/**
 * Additional components and utilities for use with SelectV2.
 */
const SelectV2Components = {
    /**
     * Pre-styled option item for custom rendering in SelectV2.
     */
    Option: PopoverItem,
    TagOption,
};

export { SelectV2, SelectV2Components };
